import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Lock, Users, Search, Archive, ArchiveRestore, Trash2, UserPlus, Copy, Send, X, Link2, MessagesSquare, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { twineAPI, twineChanged } from '@/lib/twineAPI';
import { yarnsAPI } from '@/lib/yarnsAPI';
import { getCurrentUser } from '@/lib/auth';
import { safeFormat } from '@/lib/utils';
import YarnThread from './YarnThread';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const LINK_LABELS = { contact: 'Contact', interview: 'Interview', task: 'Task', project: 'Project' };
const LINK_PATHS = { interview: (id) => `/interviews/${id}`, contact: () => '/contacts', task: () => '/tasks', project: () => '/dashboard' };

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const KindBadge = ({ kind }) => (kind === 'open'
  ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold"><Users className="w-3 h-3" />Open · client can see</span>
  : <span className="inline-flex items-center gap-1 rounded-full bg-[#F3EFE7] text-[#1E2A33] border border-[#E3DDD1] px-2 py-0.5 text-[11px] font-semibold"><Lock className="w-3 h-3" />Crew · team only</span>);

// ── New yarn ─────────────────────────────────────────────────────────────────
export const NewYarnDialog = ({ open, onOpenChange, link, onCreated }) => {
  const blank = { title: '', kind: 'crew', first_post: '', guests: [{ name: '', email: '', phone: '' }] };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ ...blank, title: link?.label ? `${link.label}` : '' }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!f.title.trim()) return toast.error('Give the yarn a title');
    const guests = f.kind === 'open' ? f.guests.filter((g) => g.name.trim() && (g.email.trim() || g.phone.trim())) : [];
    if (f.kind === 'open' && !guests.length) return toast.error('Add at least one client with a name and email');
    setSaving(true);
    try {
      const r = await yarnsAPI.create({ title: f.title, kind: f.kind, first_post: f.first_post || null, link: link || null,
        guests: guests.map((g) => ({ name: g.name, email: g.email || null, phone: g.phone || null })) });
      toast.success(f.kind === 'open' ? 'Yarn started. The client has been sent their link.' : 'Yarn started');
      onOpenChange(false);
      onCreated && onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the yarn'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Start a yarn</DialogTitle>
          <DialogDescription>One conversation about one thing. Everyone in it adds messages and files.{link ? ` Linked to ${LINK_LABELS[link.type]?.toLowerCase()}: ${link.label}.` : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[{ id: 'crew', icon: Lock, t: 'Crew', d: 'Team only. Clients never see it.' }, { id: 'open', icon: Users, t: 'Open', d: 'Includes a client by secure link.' }].map((k) => (
              <button key={k.id} type="button" onClick={() => setF({ ...f, kind: k.id })}
                className={`text-left rounded-lg border p-3 ${f.kind === k.id ? 'border-[#1E2A33] bg-[#FBF8F2]' : 'border-[#E3DDD1]'}`}>
                <div className="flex items-center gap-1.5 font-semibold text-sm"><k.icon className="w-4 h-4" />{k.t}</div>
                <div className="text-xs text-[#5C6670] mt-0.5">{k.d}</div>
              </button>
            ))}
          </div>
          {f.kind === 'crew' && <p className="text-xs text-[#5C6670]">A crew yarn stays team-only for good, so nothing said here can be shown to a client later.</p>}
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Night Drive EP launch" /></div>
          {f.kind === 'open' && (
            <div>
              <Label>Client(s)</Label>
              <div className="space-y-2 mt-1">
                {f.guests.map((g, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2">
                    <Input placeholder="Name" value={g.name} onChange={(e) => setF({ ...f, guests: f.guests.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                    <Input placeholder="Email" value={g.email} onChange={(e) => setF({ ...f, guests: f.guests.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)) })} />
                    <Input placeholder="Mobile (optional)" value={g.phone} onChange={(e) => setF({ ...f, guests: f.guests.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)) })} />
                  </div>
                ))}
                <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => setF({ ...f, guests: [...f.guests, { name: '', email: '', phone: '' }] })}>+ Add another client</button>
              </div>
              <p className="text-xs text-[#5C6670] mt-1">Each client gets their own link by email. No account needed, and you can remove them any time.</p>
            </div>
          )}
          <div><Label>First message (optional)</Label><Textarea rows={3} value={f.first_post} onChange={(e) => setF({ ...f, first_post: e.target.value })} /></div>
          <Button onClick={create} disabled={saving} className={`w-full ${red}`}>{saving ? 'Starting…' : 'Start the yarn'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Yarns linked to a record (used on other pages) ────────────────────────────
export const YarnsPanel = ({ link }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => yarnsAPI.list({ link_type: link.type, link_id: link.id }).then((r) => setRows(r.data)).catch(() => setRows([])), [link.type, link.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="rounded-xl border border-[#E3DDD1] bg-white p-4 md:p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#1E2A33] flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-[#A62B1F]" />Yarns</h3>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" />Start a yarn</Button>
      </div>
      {rows === null ? <p className="text-sm text-[#5C6670]">Loading…</p> : rows.length === 0 ? (
        <p className="text-sm text-[#5C6670]">No conversations about this yet.</p>
      ) : (
        <ul className="divide-y divide-[#E3DDD1]">
          {rows.map((y) => (
            <li key={y.id}>
              <button className="w-full text-left py-2 flex items-center gap-3" onClick={() => navigate(`/yarns/${y.id}`)}>
                {y.unread && <span className="w-2 h-2 rounded-full bg-[#A62B1F]" />}
                <span className="font-medium text-sm text-[#1E2A33] truncate">{y.title}</span>
                <KindBadge kind={y.kind} />
                <span className="ml-auto text-xs text-[#5C6670] shrink-0">{safeFormat(y.last_post_at, 'MMM d')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <NewYarnDialog open={open} onOpenChange={setOpen} link={link} onCreated={(y) => navigate(`/yarns/${y.id}`)} />
    </div>
  );
};

// ── Guests (clients) on an open yarn ─────────────────────────────────────────
const GuestsDialog = ({ open, onOpenChange, yarn, onChanged }) => {
  const [g, setG] = useState({ name: '', email: '', phone: '' });
  const add = async () => {
    try {
      await yarnsAPI.addGuest(yarn.id, { name: g.name, email: g.email || null, phone: g.phone || null });
      toast.success(`${g.name} added and sent their link`);
      setG({ name: '', email: '', phone: '' });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Clients in this yarn</DialogTitle><DialogDescription>Each client has their own secure link. Removing someone shuts their link off straight away.</DialogDescription></DialogHeader>
        <ul className="divide-y divide-[#E3DDD1] text-sm">
          {(yarn?.guests || []).map((x) => (
            <li key={x.id} className="py-2 flex items-center gap-2 flex-wrap">
              <div className="min-w-0"><div className="font-medium">{x.name}</div><div className="text-xs text-[#5C6670]">{x.email || x.phone}{x.last_seen_at ? ` · last opened ${safeFormat(x.last_seen_at, 'MMM d, h:mm a')}` : ' · not opened yet'}</div></div>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" title="Copy their link" onClick={async () => { const r = await yarnsAPI.guestLink(yarn.id, x.id); navigator.clipboard.writeText(r.data.link); toast.success('Link copied'); }}><Copy className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Resend their link" onClick={async () => { await yarnsAPI.resendGuest(yarn.id, x.id); toast.success('Sent'); }}><Send className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Remove" className="text-[#A62B1F]" onClick={async () => { if (!window.confirm(`Remove ${x.name}? Their link stops working.`)) return; await yarnsAPI.removeGuest(yarn.id, x.id); onChanged(); }}><X className="w-4 h-4" /></Button>
              </div>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-3 gap-2 pt-2">
          <Input placeholder="Name" value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} />
          <Input placeholder="Email" value={g.email} onChange={(e) => setG({ ...g, email: e.target.value })} />
          <Input placeholder="Mobile" value={g.phone} onChange={(e) => setG({ ...g, phone: e.target.value })} />
        </div>
        <Button onClick={add} disabled={!g.name.trim() || !(g.email.trim() || g.phone.trim())} className={red}><UserPlus className="w-4 h-4 mr-1" />Add client</Button>
      </DialogContent>
    </Dialog>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Yarns() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = getCurrentUser();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | crew | open | archived
  const [yarn, setYarn] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);

  const loadList = useCallback(() => yarnsAPI.list({ archived: filter === 'archived' }).then((r) => setRows(r.data)).catch(() => setRows([])), [filter]);
  const loadYarn = useCallback(() => (id ? yarnsAPI.get(id).then((r) => setYarn(r.data)).catch(() => { toast.error('Yarn not found'); navigate('/yarns'); }) : setYarn(null)), [id, navigate]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadYarn(); }, [loadYarn]);
  // Light polling so new messages show up without a refresh
  useEffect(() => {
    const t = setInterval(() => { loadList(); if (id) loadYarn(); }, 15000);
    return () => clearInterval(t);
  }, [id, loadList, loadYarn]);
  useEffect(() => { if (yarn) setRows((rs) => (rs || []).map((r) => (r.id === yarn.id ? { ...r, unread: false } : r))); }, [yarn]);

  const shown = (rows || []).filter((y) => (filter === 'crew' ? y.kind === 'crew' : filter === 'open' ? y.kind === 'open' : true))
    .filter((y) => !q || y.title.toLowerCase().includes(q.toLowerCase()) || (y.last_post_preview || '').toLowerCase().includes(q.toLowerCase()));

  const send = async (text, files) => { await yarnsAPI.post(yarn.id, text, files); await loadYarn(); loadList(); };
  const openFile = async (f) => { const r = await yarnsAPI.file(yarn.id, f.id).catch(() => null); if (r) saveBlob(r.data, f.filename); };
  const update = async (data, msg) => {
    try { await yarnsAPI.update(yarn.id, data); if (msg) toast.success(msg); await loadYarn(); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  return (
    <Layout title="Yarns">
      <div className="flex gap-4 h-[calc(100vh-140px)] min-h-[480px]">
        {/* List */}
        <div className={`${id ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-[340px] shrink-0 rounded-xl border border-[#E3DDD1] bg-white`}>
          <div className="p-3 border-b border-[#E3DDD1] space-y-2">
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-semibold text-[#1E2A33]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Yarns</h1>
              <Button size="sm" className={red} onClick={() => setNewOpen(true)}><Plus className="w-4 h-4 mr-1" />New</Button>
            </div>
            <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6670]" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search yarns" className="pl-8 h-9" /></div>
            <div className="flex gap-1 flex-wrap">
              {[['all', 'All'], ['crew', 'Crew'], ['open', 'Open'], ['archived', 'Archived']].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className={`px-2.5 py-1 rounded-full text-xs border ${filter === k ? 'bg-[#1E2A33] text-white border-[#1E2A33]' : 'text-[#5C6670] border-[#E3DDD1]'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {rows === null ? <p className="p-4 text-sm text-[#5C6670]">Loading…</p> : shown.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#5C6670]"><MessagesSquare className="w-7 h-7 mx-auto mb-2 text-[#A62B1F]" />No yarns here yet. Start one to keep a conversation and its files in one place.</div>
            ) : shown.map((y) => (
              <button key={y.id} onClick={() => navigate(`/yarns/${y.id}`)}
                className={`w-full text-left px-3 py-3 border-b border-[#E3DDD1] hover:bg-[#FBF8F2] ${y.id === id ? 'bg-[#FBF8F2]' : ''}`}>
                <div className="flex items-center gap-2">
                  {y.unread && <span className="w-2 h-2 rounded-full bg-[#A62B1F] shrink-0" />}
                  <span className={`text-sm truncate ${y.unread ? 'font-bold' : 'font-semibold'} text-[#1E2A33]`}>{y.title}</span>
                  {y.kind === 'open' ? <Users className="w-3.5 h-3.5 text-amber-700 shrink-0" title="Open: client can see" /> : <Lock className="w-3.5 h-3.5 text-[#5C6670] shrink-0" title="Crew: team only" />}
                  <span className="ml-auto text-[11px] text-[#5C6670] shrink-0">{safeFormat(y.last_post_at, 'MMM d')}</span>
                </div>
                <div className="text-xs text-[#5C6670] truncate mt-0.5">{y.last_post_preview}</div>
                {y.link && <div className="text-[11px] text-[#5C6670] mt-0.5 flex items-center gap-1"><Link2 className="w-3 h-3" />{LINK_LABELS[y.link.type]}: {y.link.label}</div>}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`${id ? 'flex' : 'hidden md:flex'} flex-1 flex-col rounded-xl border border-[#E3DDD1] bg-white min-w-0`}>
          {!yarn ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#5C6670] p-6 text-center">Pick a yarn, or start a new one.<br />Crew yarns are team-only. Open yarns include a client by secure link.</div>
          ) : (
            <>
              <div className="p-3 md:p-4 border-b border-[#E3DDD1]">
                <div className="flex items-start gap-2 flex-wrap">
                  <button className="md:hidden p-1 -ml-1" onClick={() => navigate('/yarns')}><ArrowLeft className="w-4 h-4" /></button>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-[#1E2A33] text-lg truncate">{yarn.title}</h2>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <KindBadge kind={yarn.kind} />
                      {yarn.kind === 'open' && <span className="text-xs text-[#5C6670]">with {yarn.guests.map((g) => g.name).join(', ') || 'no clients yet'}</span>}
                      {yarn.link && <button className="text-xs text-[#A62B1F] inline-flex items-center gap-1" onClick={() => navigate(LINK_PATHS[yarn.link.type](yarn.link.id))}><Link2 className="w-3 h-3" />{LINK_LABELS[yarn.link.type]}: {yarn.link.label}</button>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {yarn.kind === 'open' && <Button size="sm" variant="outline" onClick={() => setGuestsOpen(true)}><UserPlus className="w-4 h-4 mr-1" />Clients</Button>}
                    {yarn.kind === 'open' && <Button size="sm" variant="ghost" title="Close to team only" onClick={() => { if (window.confirm('Close this yarn to the team only? Every client loses access straight away. This can\'t be undone.')) update({ kind: 'crew' }, 'Closed to the team'); }}><Lock className="w-4 h-4" /></Button>}
                    {yarn.link && (
                      <Button size="sm" variant="ghost" title="Remember from this: suggest what's worth keeping in the Twine" onClick={async () => {
                        try {
                          const r = await twineAPI.mend('yarn', yarn.id);
                          const n = r.data.suggested.length;
                          toast.success(n ? `${n} suggestion${n > 1 ? 's' : ''} sent to the Twine to review` : 'Nothing new to remember');
                          twineChanged();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read it'); }
                      }}><Sparkles className="w-4 h-4" /></Button>
                    )}
                    <Button size="sm" variant="ghost" title={yarn.archived ? 'Unarchive' : 'Archive'} onClick={() => update({ archived: !yarn.archived }, yarn.archived ? 'Unarchived' : (yarn.link ? 'Archived. The Twine will suggest anything worth remembering.' : 'Archived'))}>{yarn.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}</Button>
                    {(yarn.created_by?.id === me?.id || ['owner', 'admin'].includes(me?.role)) && (
                      <Button size="sm" variant="ghost" title="Delete" className="text-[#A62B1F]" onClick={async () => { if (!window.confirm('Delete this yarn and all its messages and files?')) return; await yarnsAPI.remove(yarn.id); toast.success('Deleted'); navigate('/yarns'); loadList(); }}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-1 min-h-0 p-3 md:p-4">
                <YarnThread posts={yarn.posts} isMine={(p) => p.author.kind === 'user' && p.author.id === me?.id}
                  onSend={send} onOpenFile={openFile} disabled={yarn.archived}
                  disabledNote="This yarn is archived. Unarchive it to add to it."
                  placeholder={yarn.kind === 'open' ? 'Add to the yarn. The client will see this.' : 'Add to the yarn (team only)'} />
              </div>
            </>
          )}
        </div>
      </div>
      <NewYarnDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(y) => { loadList(); navigate(`/yarns/${y.id}`); }} />
      {yarn && <GuestsDialog open={guestsOpen} onOpenChange={setGuestsOpen} yarn={yarn} onChanged={() => { loadYarn(); loadList(); }} />}
    </Layout>
  );
}
